import {isNotNullOrUndefined} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import $ from 'jquery';
import PropTypes from 'prop-types';
import React, {ReactNode} from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as contextmenu from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {SEARCH_RESULT_COLOR, SELECTED_SEARCH_RESULT_COLOR} from 'notebook/mediocre/search/SearchConstants';
import {getSearchResultsAtRoot, MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {
  SORT_ASCENDING_ID,
  SORT_DESCENDING_ID,
} from 'notebook/structured-tables/column-sorting/getColumnSortingMenuOptions';
import columnDefinitionToTooltip from 'notebook/structured-tables/columns/columnDefinitionToTooltip';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import {
  logColumnAnalytics,
  StructuredTableAnalyticsParams,
} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import StructuredTableContext from 'notebook/structured-tables/util/StructuredTableContext';
import TableAxisCellDragHandle from 'notebook/tables/core-ui/TableAxisCellDragHandle';
import excess from 'notebook/tables/excess';
import * as TableConstants from 'notebook/tables/TableConstants';
import {Cell, ColumnConstraint, SelectionRange} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import getDimensionalOperationText from 'notebook/tables/util/getDimensionalOperationText';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BLUE_LIGHT} from 'ui-library/Colors';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as modals from 'util/Modals';
import removeNulls from 'util/removeNulls';
import {BasicEvent, ElementMouseEvent} from 'util/Types';

// TODO(BNCH-108093): The TableAxisCell component currently decides the ordering of all the context menu
// options, but in a very strange way. This needs to be fixed so that reordering these options is much easier.
// These constants are here rather than their owning Context Options to prevent circular imports.
export const SINGLE_INPUT_CONTEXT_MENU_OPTION_ID = 'structured-table-single-input';
export const ADD_EDIT_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID = 'structured-table-add-edit-table-mapping';
export const REMOVE_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID = 'structured-table-remove-table-mapping';
export const ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID = 'structured-table-add-from-structured-table';
export const CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID = 'change-custom-column-type';

// When a table is protected because it's a fill-in entry, we should only allow column context menu options
// that cannot change the structure of the table.
export const ALLOWED_COLUMN_CONTEXT_MENU_OPTION_IDS_WHEN_PROTECTED = [
  ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID,
  SORT_ASCENDING_ID,
  SORT_DESCENDING_ID,
];

export const INSERT_CUSTOM_COLUMN_LEFT = 'Insert custom column left';
export const INSERT_CUSTOM_SINGLE_INPUT_LEFT = 'Insert custom single input left';
export const INSERT_CUSTOM_COLUMN_RIGHT = 'Insert custom column right';
export const INSERT_CUSTOM_SINGLE_INPUT_RIGHT = 'Insert custom single input right';

export type AxisType = TableConstants.TableDimension;
type Direction = 'BEFORE' | 'AFTER';

const {TABLE_ROW, TABLE_COLUMN} = TableConstants;

export const enum DIRECTION {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
}

const ARCHIVED_ROW_TOOLTIP_FIRST_LINE = 'Archived';
export const UNSUBMITTED_CHANGES_TOOLTIP = 'Please submit table'; // exported for test
const TOMBSTONE_REASON_TOOLTIP = 'Row is invalid. Please adjust any formulas before deleting row.';
const ROW_ERROR_TOOLTIP = 'Error in row';

export type MenuOption =
  | '-' // specifically, a menu separator
  | {
      name: string;
      click: (e: ElementMouseEvent) => void;
      hide: () => boolean;
      disabled?: boolean;
    };

export type MenuOptions = Array<MenuOption>;

interface AnalyticsParams {
  fileAnalyticsParams: FileAnalyticsParams | null;
  structuredTableAnalyticsParams: StructuredTableAnalyticsParams | null;
}

export interface TableAxisCellProps extends MediocreSearchResultProps {
  index: number;
  label?: string | null;
  shouldHighlightCell: boolean;
  showExcessLabels: boolean;
  iconPrefix?: BenchlingIconName | null;
  renderedLabel?: ReactNode;
  dim: AxisType;
  toggleHeader?: () => void;
  delDim?: ((minIndex: number | null, maxIndex: number | null) => void) | null;
  renameDim?: (index: number, name: string) => void;
  insertDimBefore?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: TableConstants.InsertableColumnInfo | {}
  ) => void;
  insertDimAfter?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: TableConstants.InsertableColumnInfo | {}
  ) => void;
  getSelectionRange?: (dimension: AxisType, index?: number) => TableConstants.SelectionRange | null;
  numDims: number;
  deleteTable?: (() => void) | null;
  isTableSelected?: boolean;
  allowEmpty: boolean;
  disallowCustomColumns: boolean;
  cellDragStart?: (
    options: {start: [number, number] | null; active: string},
    event: React.MouseEvent
  ) => void;
  cellDragOver?: (index: [number, number] | null, e: React.MouseEvent) => void;
  showDragHandle?: boolean;
  dragHandleMouseDown?: (draggingDimension: TableConstants.TableDimension, index: number) => boolean;
  isFirstColumn?: boolean;
  isSelected: boolean;
  isGhostCell: boolean;
  columnConstraint?: TableConstants.ColumnConstraint;
  rowData?: TableConstants.RowData | null;
  rowProperties?: TableConstants.RowProperties | null;
  columnIsOutdated?: boolean;
  isLeftAlign?: boolean;
  isHeaderCell?: boolean;
  isSingleInputHeader?: boolean;
  insertableColumnInfos?: Array<TableConstants.InsertableColumnInfo>;
  getExtraContextMenuOptions?: (
    selectionRange: TableConstants.SelectionRange | null
  ) => Array<contextmenu.ContextMenuOption> | null;
  getDisallowedColumnNames?: () => Array<string>;
  shouldDisableDeleteDim: boolean;
  shouldDisableInsertDimBefore: boolean;
  shouldDisableInsertDimAfter: boolean;
  disableInsertDimTooltip?: string;
  finalizeAndExitEditMode?: () => void;
  locked?: boolean;
  isProtected?: boolean;
  disableColumnLocking?: boolean;
  tooltipIcons?: Array<TableConstants.TooltipIcon>;
  dataTestAttrs?: DataTestAttrs;
  /**
   * isConfig indicates if this cell is in a template, it will be used to determine if the column locking
   * options should be shown or not.
   */
  isConfig?: boolean;
  setColumnConstraint?: (
    index: number,
    columnConstraint: ColumnConstraint,
    headerRowCell: Partial<Cell>
  ) => void;

  // Has the row been in the view port of the browser yet?
  hasRowBeenInView?: boolean;

  // Affixed Column
  isAffixed?: boolean; // Whether the column is sticky.  Default is false
  showAffixedShadow?: boolean; // Only enabled when a column is sticky and scrolled.  Default is false.
  affixedLeftSpacing?: number; // Must be specified if isAffixed = true
}

class TableAxisCell extends React.PureComponent<TableAxisCellProps> {
  static propTypes = {
    index: PropTypes.number,
    // Editable name of this column / row; only needed if `renameDim` is specified
    label: PropTypes.string,
    shouldHighlightCell: PropTypes.bool,
    // Indicates the user is interacting with a formula, threaded from SpreadsheetEvalMixin
    showExcessLabels: PropTypes.bool.isRequired,
    iconPrefix: PropTypes.string,
    // Custom label represented as a React node
    renderedLabel: PropTypes.node,
    dim: PropTypes.oneOf([TABLE_ROW, TABLE_COLUMN]),
    toggleHeader: PropTypes.func,
    // delDim, insertDimBefore and insertDimAfter are not required since they may not be specified if
    // the table is not editable
    delDim: PropTypes.func,
    renameDim: PropTypes.func,
    insertDimBefore: PropTypes.func,
    insertDimAfter: PropTypes.func,
    getSelectionRange: PropTypes.func,
    numDims: PropTypes.number.isRequired, // number of the dim in the table
    deleteTable: PropTypes.func,
    isTableSelected: PropTypes.bool,
    allowEmpty: PropTypes.bool.isRequired,
    disallowCustomColumns: PropTypes.bool.isRequired,
    cellDragStart: PropTypes.func,
    cellDragOver: PropTypes.func,
    showDragHandle: PropTypes.bool,
    dragHandleMouseDown: PropTypes.func,
    isFirstColumn: PropTypes.bool,
    isSelected: PropTypes.bool.isRequired,
    isGhostCell: PropTypes.bool.isRequired, // a placeholder cell outside of the table's bounds
    columnConstraint: TableConstants.columnConstraint,
    rowData: TableConstants.ROW_DATA_SHAPE,
    rowProperties: TableConstants.ROW_PROPERTIES_SHAPE,
    isLeftAlign: PropTypes.bool,
    isHeaderCell: PropTypes.bool,
    isSingleInputHeader: PropTypes.bool,
    insertableColumnInfos: PropTypes.arrayOf(TableConstants.insertableColumnInfo.isRequired),
    // Method that given the selection range returns additional menu items
    // to display in the context menu. The items are of the form:
    //  name: PropTypes.string.isRequired,
    //  click: PropTypes.func,
    //  hide: PropTypes.func,
    //  displayGroup: ContextMenuDisplayGroup.TOP | ContextMenuDisplayGroup.MIDDLE | ContextMenuDisplayGroup.BOTTOM
    // or a '-' to represent a separator.
    getExtraContextMenuOptions: PropTypes.func,
    // For performance, we pass this as a function rather than an array of strings, so this prop
    // should not be used during render!
    getDisallowedColumnNames: PropTypes.func,
    shouldDisableDeleteDim: PropTypes.bool.isRequired,
    shouldDisableInsertDimBefore: PropTypes.bool.isRequired,
    shouldDisableInsertDimAfter: PropTypes.bool.isRequired,
    locked: PropTypes.bool,
    isProtected: PropTypes.bool,
    disableColumnLocking: PropTypes.bool,
    dataTestAttrs: PropTypes.object,
    isConfig: PropTypes.bool,
    setColumnConstraint: PropTypes.func,
  };

  static defaultProps = {
    isGhostCell: false,
    allowEmpty: false,
    disallowCustomColumns: false,
    allowNoRows: false,
    isLeftAlign: false,
    disableFormulas: false,
    showExcessLabels: false,
  };

  contextMenuButton = React.createRef<HTMLDivElement>();

  _getSelectionRange(): TableConstants.SelectionRange | null {
    if (!this.props.getSelectionRange) {
      return null;
    }
    return this.props.getSelectionRange(this.props.dim, this.props.index);
  }

  _delDimHandler = ({fileAnalyticsParams, structuredTableAnalyticsParams}: AnalyticsParams): void => {
    const range = this._getSelectionRange();
    if (range != null) {
      // TODO(T7364): Check that delDim is defined since the props can change after the context
      // menu is opened.
      if (typeof this.props.delDim === 'function') {
        this.props.delDim(range.minIndex, range.maxIndex);
        if (this.props.dim === TableConstants.TableDimension.COLUMN) {
          logColumnAnalytics({
            columnConstraint: this.props.columnConstraint,
            analyticsEvent: ELN_ANALYTICS_ACTIONS.DELETE_COLUMN,
            structuredTableAnalyticsParams,
            fileAnalyticsParams,
          });
        }
      }
    }
  };

  _setProtected = (
    isProtected: boolean,
    {fileAnalyticsParams, structuredTableAnalyticsParams}: AnalyticsParams
  ): void => {
    if (!this.props.setColumnConstraint) {
      return;
    }

    if (this.props.columnConstraint?.structuredTableColumn) {
      this.props.setColumnConstraint(
        this.props.index,
        reactUpdate(this.props.columnConstraint, {
          structuredTableColumn: {$merge: {isProtected}},
          $merge: {isProtected},
        }),
        {}
      );
    } else {
      this.props.setColumnConstraint(
        this.props.index,
        reactUpdate(this.props.columnConstraint || {}, {$merge: {isProtected}}),
        {}
      );
    }
    logColumnAnalytics({
      columnConstraint: this.props.columnConstraint,
      analyticsEvent: ELN_ANALYTICS_ACTIONS.TOGGLED_COLUMN_LOCKING,
      structuredTableAnalyticsParams,
      fileAnalyticsParams,
      additionalMetadata: {
        setting: isProtected ? 'lock' : 'unlock',
      },
    });
  };

  _canSetProtected(selectionRange: SelectionRange | null): boolean {
    return Boolean(this.props.isConfig) && selectionRange?.maxIndex === selectionRange?.minIndex;
  }

  /**
   * If we can only insert specific columns, not custom columns, then the "insert multiple columns"
   * UI (selecting multiple columns and inserting) should instead prompt to insert a single column,
   * and we insert to the left/right of the whole selection.
   */
  _shouldAllowInsertMultipleDims(): boolean {
    return !this.props.disallowCustomColumns;
  }

  _insertDimBeforeHandler = (
    e: BasicEvent,
    columnInfo: TableConstants.InsertableColumnInfo | {} = {}
  ): void => {
    const range = this._getSelectionRange();
    if (range != null) {
      // TODO(T7364): Check that insertDimBefore is defined since the props can change after the
      // context menu is opened.
      if (typeof this.props.insertDimBefore === 'function') {
        if (this._shouldAllowInsertMultipleDims()) {
          this.props.insertDimBefore(range.minIndex, range.maxIndex, columnInfo);
        } else {
          // If we aren't allowed to insert multiple, just insert one before the selection start.
          this.props.insertDimBefore(range.minIndex, range.minIndex, columnInfo);
        }
      }
    }
  };

  _insertDimAfterHandler = (e: BasicEvent, columnInfo?: TableConstants.InsertableColumnInfo): void => {
    const range = this._getSelectionRange();
    if (range != null) {
      // TODO(T7364): Check that insertDimAfter is defined since the props can change after the
      // context menu is opened.
      if (typeof this.props.insertDimAfter === 'function') {
        if (this._shouldAllowInsertMultipleDims()) {
          this.props.insertDimAfter(range.minIndex, range.maxIndex, columnInfo || {});
        } else {
          // If we aren't allowed to insert multiple, just insert one after the selection end.
          this.props.insertDimAfter(range.maxIndex, range.maxIndex, columnInfo || {});
        }
      }
    }
  };

  _inSelectionRange(): boolean {
    const range = this._getSelectionRange();
    if (range != null && this.props.index !== undefined) {
      return range.minIndex <= this.props.index && this.props.index <= range.maxIndex;
    }
    return false;
  }

  _getStartIndex(): [number, number] {
    switch (this.props.dim) {
      case TABLE_ROW:
        return [this.props.index, 0];
      case TABLE_COLUMN:
        return [0, this.props.index];
      default:
        assert(false, `Unexpected dimension ${this.props.dim}`);
    }
  }

  _cellDragStartHandler = (e: ElementMouseEvent): void => {
    this.props.finalizeAndExitEditMode?.();

    if (this.props.cellDragStart == null) {
      return;
    }
    // Return if the button was initiated by an open contextmenu click
    const fromMenuClick =
      $(e.target).closest(ReactDOM.findDOMNode(this.contextMenuButton.current)).length !== 0;
    if ((fromMenuClick || e.button !== 0) && this._inSelectionRange()) {
      return;
    }
    this.props.cellDragStart(
      {
        start: this._getStartIndex(),
        active: TableUtil.dimensionToSelection(this.props.dim),
      },
      e
    );
  };

  _cellDragOverHandler = (e: ElementMouseEvent): void => {
    this.props.cellDragOver?.(this._getStartIndex(), e);
  };

  _dimName(): AxisType {
    if (this.props.dim === TABLE_ROW) {
      return TABLE_ROW;
    } else {
      return TABLE_COLUMN;
    }
  }

  /**
   * Return the context menu delete column/row[s] text.
   */
  _contextMenuDeleteDimsText(): string {
    const verb = this.props.columnConstraint && this.props.columnConstraint.viewer ? 'Hide' : 'Delete';
    return getDimensionalOperationText({
      verb,
      selectionRange: this._getSelectionRange(),
      dimension: this._dimName(),
    });
  }

  _selectedCount(): number {
    const selectionRange = this._getSelectionRange();
    if (selectionRange == null) {
      return 0;
    }
    return selectionRange.maxIndex - selectionRange.minIndex + 1;
  }

  /**
   * Return the context menu insert column/row[s] before text.
   */
  _contextMenuInsertDimsText(direction: Direction): string {
    const dimension = this.props.dim === TABLE_ROW ? TABLE_ROW : TABLE_COLUMN;
    const directionText = {
      [TABLE_ROW]: {
        [DIRECTION.BEFORE]: 'above',
        [DIRECTION.AFTER]: 'below',
      },
      [TABLE_COLUMN]: {
        [DIRECTION.BEFORE]: 'left',
        [DIRECTION.AFTER]: 'right',
      },
    }[this.props.dim][direction];

    const numSelected = this._selectedCount();
    if (numSelected === 1 || !this._shouldAllowInsertMultipleDims()) {
      return `Insert ${dimension} ${directionText}`;
    } else {
      return `Insert ${numSelected} ${dimension}s ${directionText}`;
    }
  }

  _willInsertExceedTableMaxDims(): boolean {
    const numSelected = this._selectedCount();
    if (this.props.dim === TABLE_ROW) {
      return this.props.numDims + numSelected > getMaxRowsConfig(true);
    } else {
      return this.props.numDims + numSelected > TableConstants.TABLE_MAX_COLUMNS;
    }
  }

  _getInsertDimMenu(direction: Direction): {
    name: string;
    disabled: boolean;
    click?: (e: ElementMouseEvent) => void;
    menu?: null | Array<{
      name: string;
      click: (e: ElementMouseEvent) => void;
    }>;
    tooltip?: string | null;
    dataTestAttrs: DataTestAttrs;
  } {
    const name = this._contextMenuInsertDimsText(direction);
    const dataTestAttrs = {component: 'ContextMenu', element: 'insertDimMenu', key: direction};
    const insertHandler =
      direction === DIRECTION.BEFORE ? this._insertDimBeforeHandler : this._insertDimAfterHandler;
    if (this._willInsertExceedTableMaxDims()) {
      return {
        name,
        disabled: true,
        tooltip: this._contextMenuInsertDimsExceedMaxText(),
        dataTestAttrs,
      };
    } else {
      const isDisabled =
        direction === DIRECTION.BEFORE
          ? this.props.shouldDisableInsertDimBefore
          : this.props.shouldDisableInsertDimAfter;
      return {
        name,
        click: insertHandler,
        menu: isDisabled ? null : this._getInsertableColumnInfosMenu(insertHandler, direction),
        disabled: isDisabled,
        tooltip: isDisabled ? this.props.disableInsertDimTooltip : null,
        dataTestAttrs,
      };
    }
  }

  _getInsertableColumnInfosMenu(
    insertHandler: (e: BasicEvent, columnInfo: TableConstants.InsertableColumnInfo) => void,
    direction: Direction
  ): null | Array<{
    name: string;
    click: (e: ElementMouseEvent) => void;
  }> {
    const {insertableColumnInfos} = this.props;
    // Only allow inserting one field at a time if special field is to be inserted
    if (
      !insertableColumnInfos ||
      insertableColumnInfos.length === 0 ||
      (this._selectedCount() !== 1 && this._shouldAllowInsertMultipleDims())
    ) {
      return null;
    }
    return insertableColumnInfos.map((columnInfo) => ({
      name: columnInfo.columnLabels,
      click: (e: ElementMouseEvent) => {
        insertHandler(e, columnInfo);
      },
      dataTestAttrs: {
        component: 'ContextMenu',
        element: direction === DIRECTION.BEFORE ? 'insertColumnBefore' : 'insertColumnAfter',
        key: encodeURIComponent(columnInfo.columnLabels).replace(/[^A-Z0-9]/gi, ''),
      },
    }));
  }

  _contextMenuInsertDimsExceedMaxText(): string {
    const dimension = this.props.dim === TABLE_ROW ? TABLE_ROW : TABLE_COLUMN;
    const maxNumDims =
      this.props.dim === TABLE_ROW ? getMaxRowsConfig(true) : TableConstants.TABLE_MAX_COLUMNS;

    return `Total ${dimension}s after insert cannot exceed ${maxNumDims}`;
  }

  // This method should be exposed as props.onRenameModal to avoid handling special cases (of disallowed
  // names, and whether or not the label can be reset).
  _openRenameModal = (): void => {
    const {label, getDisallowedColumnNames} = this.props;
    const disallowedColumnNames = getDisallowedColumnNames ? getDisallowedColumnNames() : [];
    const validate = (name: string): boolean =>
      name === this.props.label ||
      (!disallowedColumnNames.includes(name) &&
        name.length <= TableConstants.USER_CREATED_COLUMN_NAME_MAX_LENGTH);

    // A column label can be reset if it is explicitly set.
    let canReset = this.props.columnConstraint ? this.props.columnConstraint.canResetLabel : null;
    if (canReset == null) {
      canReset = label != null;
    }

    modals.prompt('Rename column', label, validate, {canReset}).then((name) => {
      const {renameDim, index} = this.props;
      if (renameDim && index != null && name != null) {
        renameDim(index, name);
      }
    });
  };

  /**
   * Return the options for the TableAxisCell's contextmenu.
   *
   * analyticsParams is optional only to accommodate the usage of this function in _hasContextOptions, which
   * doesn't actually render the menu. All other callers should provide it.
   */
  _contextMenuOptions(analyticsParams?: AnalyticsParams | null): Array<null | contextmenu.ContextMenuOption> {
    // When in Fill-In mode, the column context menu options are limited
    const isTableProtected = this.props.dim === TABLE_COLUMN && this.props.isProtected;
    const isReadOnly = this.props.columnConstraint?.structuredTableColumn?.isReadOnly ?? false;
    const isDimensionProtected = this.props.columnConstraint?.isProtected ?? false;
    const selectionRange = this._getSelectionRange();
    const allExtraMenuOptions = this.props.getExtraContextMenuOptions?.(selectionRange) ?? [];
    // Filter out any nulls, strings, or separators so that we only have clickable options
    let extraMenuOptions = allExtraMenuOptions.filter(
      (option): option is contextmenu.ClickableContextMenuOption => option !== '-'
    );
    // Filter out any options that are not allowed when the table is protected
    if (isTableProtected) {
      extraMenuOptions = extraMenuOptions.filter((option) =>
        ALLOWED_COLUMN_CONTEXT_MENU_OPTION_IDS_WHEN_PROTECTED.includes(option.id ?? '')
      );
    }

    // any menuOptions not specifying a displayGroup will be placed at the top
    const extraMenuOptionsAtTop = extraMenuOptions.filter(
      (option) =>
        (!option.displayGroup || option.displayGroup === contextmenu.ContextMenuDisplayGroup.TOP) &&
        // Change column type option is manually positioned after rename, so exclude it from the top group
        option.id !== CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID
    );
    // Change column type option should appear right after rename
    const changeColumnTypeOption = extraMenuOptions.find(
      (option) => option.id === CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID
    );
    const menuOptionIdsMiddleUpper = [
      ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID,
      ADD_EDIT_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
      REMOVE_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
      SINGLE_INPUT_CONTEXT_MENU_OPTION_ID,
    ];
    const extraMenuOptionsAtMiddleUpper = extraMenuOptions.filter(
      (option) =>
        option.id &&
        menuOptionIdsMiddleUpper.includes(option.id) &&
        option.displayGroup === contextmenu.ContextMenuDisplayGroup.MIDDLE
    );
    const extraMenuOptionsAtMiddleLower = extraMenuOptions.filter(
      (option) =>
        (!option.id || !menuOptionIdsMiddleUpper.includes(option.id)) &&
        option.displayGroup === contextmenu.ContextMenuDisplayGroup.MIDDLE
    );
    const menuOptionsBottomUpper = [
      INSERT_CUSTOM_COLUMN_LEFT,
      INSERT_CUSTOM_SINGLE_INPUT_LEFT,
      INSERT_CUSTOM_COLUMN_RIGHT,
      INSERT_CUSTOM_SINGLE_INPUT_RIGHT,
    ];
    const extraMenuOptionsAtBottomUpper = extraMenuOptions.filter(
      (option) =>
        menuOptionsBottomUpper.includes(option.name) &&
        option.displayGroup === contextmenu.ContextMenuDisplayGroup.BOTTOM
    );
    const extraMenuOptionsAtBottomLower = extraMenuOptions.filter(
      (option) =>
        !menuOptionsBottomUpper.includes(option.name) &&
        option.displayGroup === contextmenu.ContextMenuDisplayGroup.BOTTOM
    );

    return removeNulls([
      ...extraMenuOptionsAtTop,
      '-',
      {
        name: `Rename ${this._dimName()}`,
        click: this._openRenameModal,
        hide: () =>
          (this.props.columnConstraint &&
            (this.props.columnConstraint.editLabelDisabled || this.props.columnConstraint.viewer)) ||
          this.props.renameDim == null ||
          this.props.index == null ||
          selectionRange == null ||
          isTableProtected,
        disabled: isDimensionProtected,
        tooltip: isDimensionProtected ? 'Cannot rename a locked column' : null,
        dataTestAttrs: {component: 'ContextMenu', element: 'renameDim'},
      },
      // Change column type option appears right after rename (for custom columns only)
      changeColumnTypeOption ?? null,
      {
        name: 'Toggle header',
        click: this.props.toggleHeader,
        hide: () => this.props.toggleHeader == null,
      },
      {
        name: 'Delete table',
        click: this.props.deleteTable,
        hide: () =>
          this.props.deleteTable == null ||
          !this.props.isTableSelected ||
          this.props.locked ||
          this.props.isProtected ||
          this.props.shouldDisableDeleteDim,
      },
      {
        name: this._contextMenuDeleteDimsText(),
        click: () => {
          this._delDimHandler(analyticsParams!);
        },
        hide: () =>
          this.props.delDim == null ||
          (this.props.isTableSelected && !this.props.allowEmpty) ||
          isTableProtected,
        disabled: this.props.shouldDisableDeleteDim || isDimensionProtected,
        dataTestAttrs: {component: 'ContextMenu', element: 'deleteDim'},
      },
      '-',
      {
        name: 'Lock column',
        click: () => {
          this._setProtected(true, analyticsParams!);
        },
        hide: () =>
          isDimensionProtected ||
          !this._canSetProtected(selectionRange) ||
          this.props.disableColumnLocking ||
          this.props.locked ||
          isTableProtected,
        disabled: isReadOnly,
        tooltip: isReadOnly ? 'Cannot lock a read only column' : null,
      },
      {
        name: 'Unlock column',
        click: () => {
          this._setProtected(false, analyticsParams!);
        },
        hide: () =>
          !isDimensionProtected ||
          !this._canSetProtected(selectionRange) ||
          this.props.locked ||
          isTableProtected,
        disabled: isReadOnly,
      },
      ...extraMenuOptionsAtMiddleUpper,
      '-',
      this.props.insertDimBefore && !isTableProtected ? this._getInsertDimMenu('BEFORE') : null,
      this.props.insertDimAfter && !isTableProtected ? this._getInsertDimMenu('AFTER') : null,
      '-',
      ...extraMenuOptionsAtMiddleLower,
      '-',
      ...extraMenuOptionsAtBottomUpper,
      '-',
      ...extraMenuOptionsAtBottomLower,
    ]);
  }

  openContextMenu = (e: BasicEvent, analyticsParams: AnalyticsParams): void => {
    if (!(this._getSelectionRange() != null && this._hasContextOptions())) {
      e.preventDefault();
      return;
    }
    contextmenu.view.open(e, this._contextMenuOptions(analyticsParams), true);
  };

  _hasContextOptions(): boolean {
    // Ghost cells in plain tables should never show context options/menus.
    if (this.props.isGhostCell) {
      return false;
    }

    function filterFunc(
      option: string | MenuOption | contextmenu.ClickableContextMenuOption | null
    ): boolean {
      return typeof option === 'object' && !_.result(option, 'hide');
    }

    const options = this._contextMenuOptions().filter((option) => filterFunc(option));
    // eslint-disable-next-line no-restricted-syntax
    const flattenedArray = _.flatten(options);
    return flattenedArray.length > 0;
  }

  _renderIcons(): Array<JSX.Element> | null {
    const tooltipIcons = this.props.tooltipIcons || this.props.columnConstraint?.tooltipIcons;
    if (tooltipIcons == null) {
      return null;
    }
    return tooltipIcons.map(({icon}, i) => (
      <span key={i} className="mediocre-tableEditable-axisCell-icon u-vertical-center-items">
        <BenchlingIcon icon={icon} />
      </span>
    ));
  }

  _renderPrefixIcon(renderTooltip: boolean = true): JSX.Element | null {
    let element: JSX.Element;

    if (this.props.columnConstraint?.isProtected) {
      element = (
        <span
          className={classNames('mediocre-tableEditable-axisCell-prefix-icon', {
            'mediocre-tableEditable-axisCell--highlighted-content': this.props.shouldHighlightCell,
          })}
        >
          <BenchlingIcon icon="lock" />
        </span>
      );
    } else if (BenchlingConfig.ELN_ENABLE_COLUMN_TYPE_ICONS && this.props.iconPrefix) {
      element = (
        <BenchlingIcon
          icon={this.props.iconPrefix}
          className={classNames('mediocre-tableEditable-axisCell-prefix-icon', {
            'mediocre-tableEditable-axisCell--highlighted-content': this.props.shouldHighlightCell,
            [css(styles.columnMappingIcon)]: this.props.iconPrefix === 'column-mapping',
          })}
        />
      );
    } else {
      return null;
    }

    if (!renderTooltip) {
      return element;
    }

    const tooltipTitle = this._getTooltipTitle();
    return (
      <Tooltip
        text={
          // Provide a specific div for the tooltip text to widen it slightly
          <div className="mediocre-tableEditable-axisCell-tooltip-text">{tooltipTitle}</div>
        }
        isDisabled={!tooltipTitle}
        placement={FloatingPlacement.TOP}
      >
        {element}
      </Tooltip>
    );
  }

  _cellStyle(): React.CSSProperties {
    const styles: React.CSSProperties = {};
    if (this.props.isAffixed) {
      styles.left = `${this.props.affixedLeftSpacing}px`;
    }
    const {results, selected} = getSearchResultsAtRoot(this.props);
    if (selected) {
      styles.backgroundColor = SELECTED_SEARCH_RESULT_COLOR;
    } else if (results.length > 0) {
      styles.backgroundColor = SEARCH_RESULT_COLOR;
    }
    return styles;
  }

  _renderRequiredIndicator(): JSX.Element | null {
    return this.props.columnConstraint?.structuredTableColumn?.isRequired ? (
      <span className="u-danger-color">*</span>
    ) : null;
  }

  _getArchivedRowTooltipTitle(archivePurpose: string): string {
    return `${ARCHIVED_ROW_TOOLTIP_FIRST_LINE}\n${archivePurpose}`;
  }

  _getTooltipTitle(): string {
    if (this.props.dim === TABLE_COLUMN) {
      let tooltips: Array<string | null | undefined> = [this.props.columnConstraint?.tooltip];
      if (this.props.columnConstraint?.columnDefinition) {
        tooltips.push(columnDefinitionToTooltip(this.props.columnConstraint?.columnDefinition));
      }

      const tooltipIcons = (this.props.tooltipIcons || this.props.columnConstraint?.tooltipIcons) ?? [];
      tooltips = [...tooltips, ...tooltipIcons.map(({tooltip}) => tooltip)].filter((item) =>
        isNotNullOrUndefined(item)
      );
      return _.compact([tooltips]).join(', ');
    } else {
      if (this.props.rowProperties?.archivePurpose != null) {
        return this._getArchivedRowTooltipTitle(this.props.rowProperties.archivePurpose);
      } else if (this.props.rowProperties?.isTombstoned) {
        return this.props.rowProperties?.tombstoneReasonTooltip || TOMBSTONE_REASON_TOOLTIP;
      } else if (!this.props.rowProperties?.hasErrors && this.props.rowProperties?.hasUnsubmittedChanges) {
        return this.props.rowProperties?.unsubmittedChangesTooltip ?? UNSUBMITTED_CHANGES_TOOLTIP;
      } else if (this.props.rowProperties?.hasErrors) {
        return ROW_ERROR_TOOLTIP;
      }
      return '';
    }
  }

  _shouldHideExcessLabels(): boolean {
    // We only show the "excess label", e.g. "A" or "1" if we are in "formula mode" (showExcessLabels),
    // or there is no header text (renderedLabel == null)
    return !this.props.showExcessLabels && this.props.renderedLabel != null;
  }

  _getExcessLabel(): string {
    return this.props.dim === TABLE_ROW
      ? String(excess.util.rowIndexToNumber(this.props.index))
      : excess.util.colNumberToLetters(this.props.index);
  }

  _renderLabel(hasRowBeenInView?: boolean): ReactNode {
    if (hasRowBeenInView != null && !hasRowBeenInView) {
      // Return a skeleton chip until we're in the View and need to render the real value
      return <Chip faded={false} size={ChipSize.MINI} text="&nbsp;" />;
    }
    let visibleContent: ReactNode;
    if (this._shouldHideExcessLabels()) {
      visibleContent = this.props.renderedLabel || '\u00a0';
    } else if (this.props.renderedLabel == null) {
      visibleContent = this._getExcessLabel();
    } else {
      visibleContent = (
        <>
          {this._getExcessLabel()} {'\u2013'} {this.props.renderedLabel}
        </>
      );
    }
    return (
      <span className="mediocre-tableEditable-axisCell-labelInnerWrapper">
        {visibleContent}
        {this._renderRequiredIndicator()}
      </span>
    );
  }

  _renderLabelSizer(): ReactNode {
    // Target size is just to fit the label
    if (this.props.renderedLabel) {
      return this.props.renderedLabel;
    }
    // If there is no label, size based on the excess label
    return this._shouldHideExcessLabels() ? '\u00a0' : this._getExcessLabel();
  }

  _getValidationIndicator(): ReactNode {
    assert(
      !(Boolean(this.props.rowData) && Boolean(this.props.columnConstraint)),
      'TableAxisCell should have rowData if it appears in the primary sample column and a columnConstraint ' +
        'if it appears in the header row, but not both at the same time'
    );
    if (this.props.rowData) {
      return this.props.rowData.uiData ? this.props.rowData.uiData.userAnnotationIndicator : null;
    }
    if (this.props.columnConstraint) {
      return this.props.columnConstraint.userAnnotationIndicator;
    }
    return null;
  }

  _renderDragHandle(): ReactNode {
    if (!this.props.showDragHandle || !this.props.dragHandleMouseDown) {
      return null;
    }
    return (
      <TableAxisCellDragHandle
        dim={this.props.dim}
        onMouseDown={() => this.props.dragHandleMouseDown?.(this.props.dim, this.props.index) ?? false}
      />
    );
  }

  override render(): JSX.Element {
    return (
      <EditorPropertiesContext.Consumer>
        {({rootFolderId: projectId, fileId, templateCollectionId}) => (
          <StructuredTableContext.Consumer>
            {({structuredTableKind, provenanceUuid}) => {
              const analyticsParams: AnalyticsParams = {
                structuredTableAnalyticsParams:
                  structuredTableKind != null
                    ? {structuredTableKind, structuredTableProvenanceUuid: provenanceUuid!}
                    : null,
                fileAnalyticsParams: {fileId, projectId, templateCollectionId},
              };
              const prefixIcon = this._renderPrefixIcon();
              const content = (
                <>
                  {!this.props.isSingleInputHeader && this._renderDragHandle()}
                  {this._getValidationIndicator()}
                  <div
                    className={classNames('mediocre-tableEditable-axisCell-labelWrapper', {
                      'is-singleInput-header': this.props.isSingleInputHeader,
                    })}
                  >
                    {/* Render the typical label along with any icons, invisibly, to properly size the cell */}
                    {!this.props.isSingleInputHeader && (
                      <>
                        <div className="invisible">{this._renderPrefixIcon(false)}</div>

                        <div
                          className={classNames(
                            'mediocre-tableEditable-axisCell-labelSizer',
                            'u-overflow-wrap-break-word',
                            'invisible',
                            {
                              'mediocre-tableEditable-axisCell--header': this.props.isHeaderCell,
                            }
                          )}
                        >
                          {this._renderLabelSizer()}
                        </div>
                      </>
                    )}
                    <div className="u-flex-display invisible">{this._renderIcons()}</div>
                    {/* Depending on hover / excel-mode, render the correct label. This is absolutely positioned,
          so changing the label size does not cause any jank resizes */}
                    <div
                      className={classNames(
                        'mediocre-tableEditable-axisCell-label',
                        'u-vertical-center-items',
                        {
                          'is-singleInput-header': this.props.isSingleInputHeader,
                          'is-singleInput-header-without-excess-label':
                            this.props.isSingleInputHeader && !this.props.showExcessLabels,
                        }
                      )}
                    >
                      {this.props.isSingleInputHeader && this._renderDragHandle()}
                      {prefixIcon}
                      <span
                        className={classNames(
                          'mediocre-tableEditable-axisCell-label-text u-white-space-pre-wrap u-overflow-wrap-break-word',
                          {
                            'text-left': this.props.isLeftAlign,
                            'mediocre-tableEditable-axisCell--header': this.props.isHeaderCell,
                            'mediocre-tableEditable-axisCell--highlighted-content':
                              this.props.shouldHighlightCell,
                          }
                        )}
                        {...getTestProps(this.props.dataTestAttrs)}
                      >
                        {this._renderLabel(this.props.hasRowBeenInView)}
                      </span>
                      <div className="u-flex-display">{this._renderIcons()}</div>
                      {this._hasContextOptions() ? (
                        <span
                          ref={this.contextMenuButton}
                          className={classNames(
                            'mediocre-tableEditable-axisCell-dropdown u-vertical-center-items'
                          )}
                          onClick={(e) => {
                            this.openContextMenu(e, analyticsParams);
                          }}
                          {...getTestProps({component: 'TableAxisCell', element: 'contextMenuButton'})}
                        >
                          <BenchlingIcon icon="chevron-down" />
                        </span>
                      ) : null}
                    </div>
                  </div>
                </>
              );
              const tooltipTitle = this._getTooltipTitle();
              return (
                <td
                  {...getTestProps({
                    component: 'TableAxisCell',
                    element: this.props.dim === TABLE_ROW ? 'rowheader' : 'columnheader',
                    key: `${this.props.index}`,
                  })}
                  aria-selected={this.props.isSelected}
                  role={this.props.dim === TABLE_ROW ? 'rowheader' : 'columnheader'}
                  className={classNames('mediocre-tableEditable-axisCell', {
                    'mediocre-tableEditable-axisCell--row': this.props.dim === TABLE_ROW,
                    'mediocre-tableEditable-axisCell--col': this.props.dim === TABLE_COLUMN,
                    'is-selected': this.props.isSelected,
                    'is-ghost-cell': this.props.isGhostCell,
                    'mediocre-table-row-status--rowHasWarnings': this.props.rowProperties?.hasWarnings,
                    'mediocre-table-row-status--rowIsOutdated': this.props.rowProperties?.isOutdated,
                    'mediocre-table-row-status--rowHasUnsubmittedChanges':
                      this.props.rowProperties?.hasUnsubmittedChanges,
                    'mediocre-table-row-status--rowIsTombstoned': this.props.rowProperties?.isTombstoned,
                    'mediocre-table-row-status--rowHasErrors': this.props.rowProperties?.hasErrors,
                    'mediocre-table-row-status--rowIsArchived':
                      this.props.rowProperties?.archivePurpose != null ||
                      (this.props.rowProperties?.archivePurpose != null &&
                        this.props.rowProperties?.hasErrors),
                    'mediocre-tableEditable-axisCell--columnIsOutdated': this.props.columnIsOutdated,
                    'mediocre-tableEditable-axisCell--highlighted-background': this.props.shouldHighlightCell,
                    'cell-sticky': this.props.isAffixed,
                    'cell-rightShadow': this.props.showAffixedShadow,
                    'cell-isFirstColumn': this.props.isFirstColumn,
                    'cell-isSingleInputHeader': this.props.isSingleInputHeader,
                    'cell-isSingleInputHeader-is-selected':
                      this.props.isSingleInputHeader && this.props.isSelected,
                  })}
                  onMouseDown={this._cellDragStartHandler}
                  onMouseOver={this._cellDragOverHandler}
                  style={this._cellStyle()}
                  onContextMenu={(e) => {
                    this.openContextMenu(e, analyticsParams);
                  }}
                >
                  {prefixIcon != null && content}
                  {prefixIcon == null && (
                    // There is no tooltip on this cell's icon, so show it on the axis cell.
                    // This can happen if column type icons are not enabled or if this is a row axis cell.
                    <Tooltip
                      text={
                        // Provide a specific div for the tooltip text to widen it slightly
                        <div className="mediocre-tableEditable-axisCell-tooltip-text">{tooltipTitle}</div>
                      }
                      isDisabled={!tooltipTitle}
                      placement={FloatingPlacement.TOP}
                      toggleDisplayMode={ToggleDisplayMode.BLOCK}
                    >
                      {content}
                    </Tooltip>
                  )}
                </td>
              );
            }}
          </StructuredTableContext.Consumer>
        )}
      </EditorPropertiesContext.Consumer>
    );
  }
}

const styles = StyleSheet.create({
  columnMappingIcon: {
    color: BLUE_LIGHT,
  },
});

export default TableAxisCell;
